import type { Doodad } from '../types';

export const doodads: Doodad[] = [
  {
    id: 'car-upgrade',
    type: 'doodad',
    title: 'The car upgrade feels deserved',
    description: 'The new payment is manageable. The old car was fine. The monthly nut gets bigger.',
    whisper: 'You drive every day. You\'ve earned a real seat.',
    cost: 2500,
    expenseChange: 520,
    happiness: 2,
    lesson: 'Lifestyle upgrades raise the FIRE target every month.',
  },
  {
    id: 'concert-weekend',
    type: 'doodad',
    title: 'Concert weekend with friends',
    description: 'A great memory, a hotel bill, and a credit card swipe if you are short on cash.',
    whisper: 'When was the last time you really had fun?',
    cost: 900,
    expenseChange: 0,
    happiness: 3,
    lesson: 'One-time spending is not evil, but it competes with capital.',
  },
  {
    id: 'luxury-apartment',
    type: 'doodad',
    title: 'Move into the nicer apartment',
    description: 'Better gym, better view, and a permanent increase in monthly expenses.',
    whisper: 'You\'re not a kid anymore. Live like an adult.',
    cost: 1300,
    expenseChange: 650,
    happiness: 2,
    lesson: 'Recurring doodads are more dangerous than one-time doodads.',
  },
  {
    id: 'phone',
    type: 'doodad',
    title: 'New phone on monthly plan',
    description: 'The latest model on a 24-month plan. Only a little per month, which is how the pile gets built.',
    whisper: 'Your camera roll deserves better.',
    cost: 400,
    expenseChange: 65,
    happiness: 1,
    lesson: 'Small recurring payments hide inside the cash flow statement.',
  },
  {
    id: 'boat',
    type: 'doodad',
    title: 'Used boat, "great deal"',
    description: 'The seller says boats hold value. The marina, slip fees, and maintenance disagree.',
    whisper: 'Your father will finally be proud.',
    cost: 14000,
    expenseChange: 400,
    happiness: 4,
    lesson: 'A non-asset can look like an asset when resale value is the excuse.',
  },
  {
    id: 'reliable-car',
    type: 'doodad',
    title: 'Replace the unreliable car',
    description:
      'The old car is bleeding surprise repairs. A newer used car lowers repair drama but locks in a real payment.',
    whisper: 'Peace of mind is priceless.',
    cost: 1800,
    expenseChange: 310,
    expenseReduction: 90,
    happiness: 3,
    lesson: 'Sometimes a doodad converts variable pain into a fixed obligation. Read the net cash flow.',
  },
  {
    id: 'closer-apartment',
    type: 'doodad',
    title: 'Move closer to work',
    description:
      'Shorter commute, better sleep, less friction. The rent increase is permanent.',
    whisper: 'An hour back every day is worth any rent.',
    cost: 1600,
    expenseChange: 420,
    timeChange: 1,
    happiness: 3,
    lesson: 'Buying time can be rational, but the FIRE target moves when fixed expenses rise.',
  },
  {
    id: 'house-cleaner',
    type: 'doodad',
    title: 'Hire a bi-weekly house cleaner',
    description:
      'Not an asset, but it buys back evenings and reduces domestic stress.',
    whisper: 'You make plenty. Why are you scrubbing toilets?',
    cost: 0,
    expenseChange: 360,
    timeChange: 1,
    happiness: 2,
    lesson: 'Convenience spending can buy time, but it must fit the statement.',
  },
  {
    id: 'kids-travel-sports',
    type: 'doodad',
    title: 'Kid makes the travel team',
    description:
      'The family is excited and the memories are real. Fees, hotels, and weekend travel hit cash flow.',
    whisper: 'Don\'t be the parent who said no.',
    cost: 600,
    expenseChange: 260,
    timeChange: -1,
    happiness: 4,
    lesson: 'Family spending has a strong pull because the benefit is emotional, not financial.',
  },
  {
    id: 'wedding-season',
    type: 'doodad',
    title: 'Wedding season pressure',
    description:
      'Flights, hotel, gifts, and clothes. Skipping saves money but costs social capital.',
    whisper: 'These are your people. Show up.',
    cost: 1500,
    expenseChange: 0,
    happiness: 3,
    lesson: 'One-time spending still matters when it eats capital that could have created options.',
  },
  {
    id: 'meal-service',
    type: 'doodad',
    title: 'Meal kit subscription',
    description:
      'Less grocery planning and fewer drive-through nights, but the subscription stays on the card.',
    whisper: 'Eat better, live better. Stop with the takeout.',
    cost: 0,
    expenseChange: 270,
    expenseReduction: 90,
    timeChange: 1,
    happiness: 2,
    lesson: 'A convenience doodad can reduce some waste and still raise the monthly nut.',
  },
  {
    id: 'all-inclusive-vacation',
    type: 'doodad',
    title: 'All-inclusive beach vacation',
    description: 'Two weeks somewhere warm. Beach, drinks, every meal handled. You will feel renewed for ten days.',
    whisper: 'You\'ve worked hard. You deserve this.',
    cost: 3800,
    expenseChange: 0,
    happiness: 4,
    lesson: 'Memory dividends are real but they decay. Capital compounds.',
  },
  {
    id: 'gym-trainer',
    type: 'doodad',
    title: 'Premium gym + personal trainer',
    description: 'Boutique gym, twice-weekly trainer, accountability that finally works.',
    whisper: 'Future-you will thank present-you.',
    cost: 0,
    expenseChange: 280,
    happiness: 2,
    lesson: 'Buying motivation is fine — and it is also a recurring line on the statement.',
  },
  {
    id: 'subscription-stack',
    type: 'doodad',
    title: 'Subscription stack creep',
    description: 'Five auto-renewals you forgot about: streaming, cloud storage, fitness app, news, music. Each one feels small.',
    whisper: 'It\'s only a few bucks each. Don\'t be cheap.',
    cost: 0,
    expenseChange: 145,
    happiness: 1,
    lesson: 'Auto-billed micro-spending is invisible until you total a year of it.',
  },
  {
    id: 'designer-item',
    type: 'doodad',
    title: 'Designer handbag / luxury watch',
    description: 'It signals status, fits perfectly, and you genuinely love it. Resale value is a story you tell yourself.',
    whisper: 'Think of the Insta likes.',
    cost: 2400,
    expenseChange: 0,
    happiness: 2,
    lesson: 'Status purchases compete with capital and rarely beat it for long.',
  },
  {
    id: 'therapy-retainer',
    type: 'doodad',
    title: 'Weekly therapy retainer',
    description: 'A good therapist who actually helps. The bill arrives every month, and you keep showing up.',
    whisper: 'You can\'t pour from an empty cup.',
    cost: 0,
    expenseChange: 520,
    happiness: 3,
    lesson: 'Some recurring expenses are investments in the human asset. Do not auto-optimize them away.',
  },
  {
    id: 'smart-home',
    type: 'doodad',
    title: 'Smart-home upgrade',
    description: 'Doorbell camera, smart locks, every light dimmable from your phone. Subscriptions hide inside the ecosystem.',
    whisper: 'Welcome to the future. Live in it.',
    cost: 2200,
    expenseChange: 35,
    happiness: 2,
    lesson: 'Hardware purchases now bring software fees with them — read the small print.',
  },
  {
    id: 'pet-adoption',
    type: 'doodad',
    title: 'Adopt a dog',
    description: 'A rescue with sad eyes who chose you. Food, vet, insurance, and training — and your weekends now revolve around walks.',
    whisper: 'She chose you. How can you say no?',
    cost: 500,
    expenseChange: 220,
    timeChange: -1,
    happiness: 4,
    lesson: 'Pets are family, and family expenses by another name. Worth it — but read the statement first.',
  },
  {
    id: 'holiday-shopping',
    type: 'doodad',
    title: 'Holiday shopping spree',
    description: 'Gifts for family, friends, coworkers, and a few "for me" purchases that snuck into the cart.',
    whisper: 'Be generous. They\'ll remember.',
    cost: 1400,
    expenseChange: 0,
    happiness: 3,
    lesson: 'Seasonal spending compresses a year of restraint into one weekend.',
  },
];
